import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Archive, Loader2 } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ReservedLine } from "@/components/client/ReservedLine";
import { api } from "@/lib/api";

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try { const r = await api.get("/cerca", { params: { q: term } }); if (live) setRes(r.data); }
      finally { if (live) setBusy(false); }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  const go = (link) => { setOpen(false); setQ(""); navigate(link); };
  const mac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-open"
        aria-label="Cerca" className="flex items-center justify-center md:justify-start gap-2 h-11 w-11 md:h-auto md:w-[380px] rounded-full border border-[#0A1128]/15 bg-white/80 md:px-4 md:py-2 text-sm text-slate-500 hover:border-[#E8173A] transition-colors">
        <Search className="h-5 w-5 md:h-4 md:w-4 text-[#0A1128] md:text-slate-500" /><span className="hidden md:inline">Cerca clienti, task, post, fatture…</span>
        <kbd className="core-kbd hidden md:inline ml-auto rounded-md border border-slate-300 bg-slate-50 px-1.5 text-[11px] font-semibold text-slate-600">{mac ? "⌘" : "Ctrl"} K</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-2xl top-[15%] translate-y-0" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Ricerca globale</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-[#E8173A] [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-2.5">
        <CommandInput value={q} onValueChange={setQ} placeholder="Cerca in tutta l'app…" data-testid="global-search-input" />
        <CommandList className="max-h-[60vh] max-sm:max-h-[calc(100dvh-4rem)]" data-testid="global-search-results">
          {busy && <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Ricerca…</div>}
          {!busy && q.trim().length < 2 && <div className="px-4 py-6 text-sm text-slate-500 text-center">Digita almeno 2 caratteri.</div>}
          {res && !busy && <CommandEmpty>Nessun risultato per «{res.q}».</CommandEmpty>}
          {res?.gruppi.map((g) => (
            <CommandGroup key={g.key} heading={g.label} data-testid={`search-group-${g.key}`}>
              {g.items.map((it) => (
                <CommandItem key={`${g.key}-${it.id}`} value={`${g.key}-${it.id}`} onSelect={() => go(it.link)} className="cursor-pointer" data-testid={`search-item-${g.key}-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0A1128] truncate">{it.titolo}</div>
                    {it.sottotitolo && <div className="text-xs text-slate-500 truncate">{it.sottotitolo}</div>}
                    {it.riservato_a && <ReservedLine names={it.riservato_a} testid={`search-reserved-${g.key}-${it.id}`} />}
                  </div>
                  {it.archiviato && <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800"><Archive className="h-3 w-3" />Archiviato</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
        </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
